import type { LatLngBounds } from 'leaflet';
import { pipe, catchError } from 'overmind';

import type { Context } from '@/overmind';
import type { LayerId } from '@/overmind/effects/adapters/baseadapter';
import { getRequestAborter } from '@/overmind/effects/requestAborter';
import { UnexpectedStateError, AbortError } from '@/overmind/errors';

import type { FeaturesInBoundsState } from '.';

const { switchToNewRequest, completeRequestOperator } = getRequestAborter(
  'getFeaturesInBounds'
);

export type FeaturesInBoundsActionBody = {
  bounds: LatLngBounds;
  layerIdsToSearch: LayerId[];
};

export const getFeaturesInBounds = pipe(
  async (
    { state, effects }: Context,
    { bounds, layerIdsToSearch }: FeaturesInBoundsActionBody
  ) => {
    const abortController = switchToNewRequest();
    state.arcgis.featuresInBounds.send('TRIGGER_LOADING');
    const adapter = effects.arcgisMapAdapter.getAdapter();
    if (adapter) {
      const featuresInLayers = await adapter.getFeaturesInBounds(
        bounds,
        layerIdsToSearch,
        state.calculation.gridVersionName,
        abortController
      );
      return {
        bounds,
        featuresInLayers
      };
    }

    throw new UnexpectedStateError();
  },
  ({ state }: Context, { bounds, featuresInLayers }) => {
    const newState: FeaturesInBoundsState = {
      bounds,
      features: featuresInLayers.flatMap(layer => layer.features)
    };
    state.arcgis.featuresInBounds.send('FETCH_SUCCESS', newState);
  },
  catchError(({ state }: Context, error) => {
    if (
      !(error instanceof UnexpectedStateError || error instanceof AbortError)
    ) {
      state.arcgis.featuresInBounds.send('FETCH_ERROR', error.message);
    }
  }),
  completeRequestOperator()
);

export const resetFeaturesInBoundsState = pipe(async ({ state }: Context) => {
  state.arcgis.featuresInBounds.send('UNLOAD');
});
